/* eslint-disable react/forbid-component-props */
/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */
import { Img } from '@/components/blocks/MaterialImage';
import {
	productImageBaseSliderSX,
	productImageBaseSX,
} from '@/components/blocks/ProductImage/styles/base';
import { productImageSelectedSX } from '@/components/blocks/ProductImage/styles/selected';
import { productImageThumbnailSX } from '@/components/blocks/ProductImage/styles/thumbnail';
import { Ribbon } from '@/data/types/Product';
import { combineSX } from '@/utils/combineSX';
import {
	SxProps,
	Theme,
	Box,
	IconButton,
	Stack,
	Tooltip,
	ClickAwayListener,
	useMediaQuery,
	useTheme,
	tooltipClasses,
	TooltipProps,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import ShareIcon from '@mui/icons-material/Share';
import { keyframes } from '@mui/system';
import { CounterSlides } from '@/components/content/ProductDetails/parts/CounterSlides';
import { ImageWithZoom, Slider } from 'pure-react-carousel';
import { CarouselProvider } from '@/components/blocks/Carousel/CarouselProvider';
import { CarouselSlide } from '@/components/blocks/Carousel/CarouselSlide';
import { ProductDetailsAddToWishList } from '@/components/blocks/ProductDetails/AddToWishList';
import styled from '@emotion/styled';
import { useStock } from '@/components/blocks/ProductDetails/hooks/useStock';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { ContentContext } from '@/data/context/content';
import { mergeRibbonsForProduct } from '@/utils/mergeRibbonsForProduct';
import { ProductRibbons } from '@/components/blocks/Ribbons';

const modifyVideoPlaceholderSrc = (src: string) => {
	if (src.includes('.mp4')) {
		return src.replace('.mp4', '.jpg');
	}
	return src;
};

const fadeIn = keyframes`
   from {
      opacity: 0;
      transform: scale(1);
   }
   to {
      opacity: 1;
      transform: scale(1);
   }
`;

type Props = {
	src: string;
	alt: string;
	ribbons: Ribbon[];
	isThumbnail?: boolean;
	isSelected?: boolean;
	sx?: SxProps<Theme>;
	isPDP?: boolean;
	indexImage?: number;
	totalSlides?: number;
	isTablet?: boolean;
	isCompleteLook?: boolean;
};

export const ProductImage: React.FC<Props> = ({
	src,
	alt,
	ribbons,
	isSelected,
	isThumbnail,
	sx,
	isPDP = false,
	indexImage,
	totalSlides,
}) => {
	const [animate, setAnimate] = useState(false);
	const theme = useTheme();
	const isSm = useMediaQuery(theme.breakpoints.down('md'));
	const isLg = useMediaQuery(theme.breakpoints.down('lg'));
	const [openShare, setOpenShare] = useState(false);
	const [urlCompleta, setUrlCompleta] = useState('');

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const urlActual = window.location.href;
			setUrlCompleta(urlActual);
		}
	}, []);

	useEffect(() => {
		if (src) {
			setAnimate(true);
			const timer = setTimeout(() => setAnimate(false), 300);
			return () => clearTimeout(timer);
		}
	}, [src]);
	const handleShare = () => {
		setOpenShare(true);
	};

	const handleTooltipClose = () => {
		setOpenShare(false);
	};

	const handleShareWhatsapp = () => {
		const message = encodeURIComponent(
			`¡Te recomiendo este producto que vi en dportenis! ${urlCompleta}`
		);
		const linkWhatsApp = `https://wa.me/?text=${message}`;
		window.open(linkWhatsApp, '_blank');
	};

	const handleShareFacebook = () => {
		window?.open(`https://www.facebook.com/sharer.php?s=100&u=${window?.location?.href}`, '_blank');
	};

	const sortedRibbons = mergeRibbonsForProduct(ribbons);

	const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
		<Tooltip {...props} classes={{ popper: className }} />
	))(() => ({
		[`& .${tooltipClasses.tooltip}`]: {
			backgroundColor: '#F1F1F1',
			color: 'rgba(0, 0, 0, 0.87)',
			fontSize: 'var(--font-size-tiny)',
		},
		[`& .${tooltipClasses.arrow}`]: {
			color: '#F1F1F1',
		},
	}));

	const { availability } = useContext(ContentContext) as ReturnType<typeof useProductDetails>;
	const { someWstock } = useStock(availability);

	return src ? (
		<Stack
			className="product-image-container"
			sx={{
				position: 'relative',
				width: { md: 'fit-content' },
				height: 'fit-content',
			}}
		>
			{isPDP && !isSm ? (
				<CarouselProvider
					visibleSlides={1}
					totalSlides={1}
					step={2}
					naturalSlideWidth={550}
					naturalSlideHeight={550}
					sx={combineSX([
						productImageBaseSliderSX,
						sx,
						{
							minHeight: 450,
							maxHeight: 624,
							height: isLg ? '550px' : '624px',
							width: isLg ? '550px' : '624px',
							'.slide': {
								boxSizing: 'unset',
							},
							'@media (max-width: 1000px)': {
								height: '500px',
								width: '500px',
							},
						},
						animate && {
							animation: `${fadeIn} 0.5s ease-in-out`,
						},
					])}
				>
					<Slider>
						<CarouselSlide tag="a" index={0}>
							{src.includes('.mp4') ? (
								<Box
									component="video"
									src={src}
									controls
									sx={combineSX([
										productImageBaseSX,
										isThumbnail && productImageThumbnailSX,
										isSelected && productImageSelectedSX,
										sx,
										animate && {
											animation: `${fadeIn} 0.5s ease-in-out`,
										},
									])}
								/>
							) : (
								<ImageWithZoom
									className="product-image-with-zoom"
									src={src.replace('hclstore', 'wcsstore').replace(/normal|default/g, 'zoom')}
								/>
							)}
						</CarouselSlide>
					</Slider>
				</CarouselProvider>
			) : isPDP && isSm ? (
				src.includes('.mp4') ? (
					<Box
						component="video"
						src={src}
						controls
						sx={combineSX([
							productImageBaseSX,
							isThumbnail && productImageThumbnailSX,
							isSelected && productImageSelectedSX,
							sx,
							animate && {
								animation: `${fadeIn} 0.5s ease-in-out`,
							},
						])}
					/>
				) : (
					<Img
						className="product-image-with-zoom"
						sx={combineSX([
							productImageBaseSX,
							isThumbnail && productImageThumbnailSX,
							isSelected && productImageSelectedSX,
							sx,
							animate && {
								animation: `${fadeIn} 0.5s ease-in-out`,
							},
						])}
						src={src.replace('hclstore', 'wcsstore').replace(/normal|default/g, 'zoom')}
						alt={alt}
					/>
				)
			) : src.includes('.mp4') ? (
				<Box>
					<Img
						src={modifyVideoPlaceholderSrc(src)}
						sx={combineSX([
							productImageBaseSX,
							isThumbnail && productImageThumbnailSX,
							isSelected && productImageSelectedSX,
							sx,
						])}
					/>
					<Img
						src="https://dpjye2wk9gi5z.cloudfront.net/wcsstore/DportenisStorefrontAssetStore/images/iconmonstr-video.png"
						sx={{
							position: 'absolute',
							top: '50%',
							left: '50%',
							transform: 'translate(-50%, -50%)',
							objectFit: 'cover',
							width: '50%',
							backgroundSize: 'cover',
							backgroundPosition: 'center',
							backgroundRepeat: 'no-repeat',
						}}
					/>
				</Box>
			) : (
				<Img
					sx={combineSX([
						productImageBaseSX,
						isThumbnail && productImageThumbnailSX,
						isSelected && productImageSelectedSX,
						sx,
					])}
					src={src}
					alt={alt}
				/>
			)}
			<ProductRibbons ribbons={sortedRibbons} />

			{isPDP && !isSm && someWstock ? (
				<ProductDetailsAddToWishList disabled={!someWstock} isIconFavorite />
			) : null}
			{isPDP && isSm ? (
				<CounterSlides selectedNumber={indexImage} totalSlides={totalSlides} />
			) : null}
			{isPDP && !isSm ? (
				<ClickAwayListener onClickAway={handleTooltipClose}>
					<Box>
						<LightTooltip
							open={openShare}
							sx={{
								'& .MuiTooltip-arrow': {
									backgroundColor: '#F1F1F1',
								},
							}}
							title={
								<Stack direction="row" spacing={2} padding={0.5}>
									<Img
										id="pdp-facebook-share"
										src="/images/pdp-facebook.png"
										alt="Facebook"
										sx={{ width: '27px', height: '27px', cursor: 'pointer' }}
										onClick={handleShareFacebook}
									/>
									<Img
										id="pdp-instagram-share"
										src="/images/whatsapp.png"
										alt="Instagram"
										sx={{ width: '27px', height: '27px', cursor: 'pointer' }}
										onClick={handleShareWhatsapp}
									/>
								</Stack>
							}
							arrow
						>
							<IconButton
								id="pdp-share-button"
								aria-label="share"
								sx={{
									backgroundColor: '#F1F1F1',
									position: 'absolute',
									bottom: '18px',
									left: someWstock ? '19px' : '10px',
									zIndex: 1001,
								}}
								onClick={handleShare}
							>
								<ShareIcon />
							</IconButton>
						</LightTooltip>
					</Box>
				</ClickAwayListener>
			) : null}
		</Stack>
	) : null;
};
